import React from 'react'
import {
  Box,
  Typography,
  ButtonBase,
  Button,
  CircularProgress,
} from '@mui/material'
import DoneIcon from '@mui/icons-material/Done'
import { useReceivingStore } from 'store/receiving'
import type { OwnershipType } from 'store/receiving'
import useGetLocationsTree from 'lib/services/api/product-service/getLocationsTree'
import Select from 'components/molecules/Select/Select'
import styles from './OwnershipSelection.module.scss'
import type { OwnershipSelectionProps } from './OwnershipSelection.types'

const OwnershipSelection: React.FC<OwnershipSelectionProps> = ({ onNext }) => {
  const ownershipType = useReceivingStore((s) => s.ownershipType)
  const setOwnershipType = useReceivingStore((s) => s.setOwnershipType)
  const selectedLocationId = useReceivingStore((s) => s.selectedLocationId)
  const setSelectedLocationId = useReceivingStore(
    (s) => s.setSelectedLocationId
  )
  const { data, loading: isLoading } = useGetLocationsTree()

  const rawLocations = data?.getLocationsTree.hospital.locations
  const locations =
    rawLocations
      ?.filter((item) => item.parentLocationId)
      .map((item) => ({ name: item.name, id: item.id })) || []

  const ownershipOptions: Array<{
    label: string
    value: OwnershipType
    description: string
  }> = [
    {
      label: 'Consigned Items',
      value: 'Consigned',
      description: 'Items owned by the vendor, used on loan basis',
    },
    {
      label: 'Purchased Items',
      value: 'Purchased',
      description: 'Items owned by the hospital after purchase',
    },
  ]

  const handleOwnershipSelection = (type: OwnershipType) => {
    setOwnershipType(type)
  }

  const handleLocationSelection = (option: {
    name: string
    id: number | string
  }) => {
    setSelectedLocationId(String(option.id))
  }

  const canProceed = ownershipType && selectedLocationId

  const handleContinue = () => {
    if (canProceed) {
      onNext()
    }
  }

  return (
    <Box className={styles.ownershipSelection}>
      <Typography variant="h5" align="center" className={styles.title}>
        Setup Receiving Session
      </Typography>

      <Typography
        variant="body1"
        align="center"
        color="text.secondary"
        className={styles.subtitle}
      >
        Choose the ownership type and location for your receiving session.
      </Typography>

      <Box className={styles.section}>
        <Typography variant="h6" className={styles.sectionTitle}>
          Select Location
        </Typography>
        <Select
          hasSearchIcon
          options={locations}
          onSelect={handleLocationSelection}
          label="Search locations"
          placeholder="Choose a location..."
        />
        {isLoading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>

      <Box className={`${styles.section} ${styles.small}`}>
        <Typography variant="h6" className={styles.sectionTitle}>
          Select Ownership Type
        </Typography>
        <Box className={styles.ownershipOptions}>
          {ownershipOptions.map((option) => (
            <ButtonBase
              key={option.value}
              className={`${styles.ownershipOption} ${
                ownershipType === option.value ? styles.selected : ''
              }`}
              disableRipple
              onClick={() => handleOwnershipSelection(option.value)}
            >
              <Box className={styles.optionContent}>
                <Box className={styles.optionHeader}>
                  <Box className={styles.optionCircle}>
                    {ownershipType === option.value && (
                      <DoneIcon className={styles.checkIcon} />
                    )}
                  </Box>
                  <Typography variant="h6" className={styles.optionTitle}>
                    {option.label}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  className={styles.optionDescription}
                >
                  {option.description}
                </Typography>
              </Box>
            </ButtonBase>
          ))}
        </Box>
      </Box>

      <Box display="flex" justifyContent="center">
        <Button
          variant="contained"
          size="large"
          onClick={handleContinue}
          disabled={!canProceed}
          className={styles.continueButton}
        >
          Continue
        </Button>
      </Box>
    </Box>
  )
}

export default OwnershipSelection
